import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Card, Stack, Snackbar, Alert } from "@mui/material";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import AddIcon from "@mui/icons-material/Add";
import ElectionList from "./ElectionList";
import ElectionPage from "./ElectionPage";
import ComplianceWizard from "./ComplianceWizard";
import api from "../../utils/api";

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const EVoting = () => {
  const [selectedElection, setSelectedElection] = useState(null);
  const [elections, setElections] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  useEffect(() => {
    fetchElections();
  }, []);

  const fetchElections = async () => {
    try {
      const res = await api.get("/elections");
      setElections(res.data || []);
    } catch (err) {
      console.error("Failed to load elections:", err);
      setElections([]);
      setSnackbarMessage("Failed to load elections. Please refresh.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const formatDate = (date) =>
    new Date(date).toISOString().slice(0, 19).replace("T", " ");

  // Called by ComplianceWizard when the user finishes all steps and clicks "Create Election"
  const handleCreateElection = async ({ title, isBoardElection, complianceChapter, complianceSummary }) => {
    try {
      const wizardAnswers = complianceSummary?.answers || {};
      const statute = complianceChapter === "hoa" ? "FS §720.317" : complianceChapter === "condo" ? "FS §718.128" : null;
      const res = await api.post("/elections", {
        title,
        description: statute
          ? `${statute} compliance checked - ${complianceSummary.level} (${complianceSummary.score}% score).`
          : "",
        start_datetime: formatDate(new Date()),
        end_datetime: formatDate(new Date()),
        created_by: Number(localStorage.getItem("userId")) || null,
        is_board_election: isBoardElection ? 1 : 0,
        // The chapter picked in step 0 is a jurisdiction fact, not a score —
        // it applies (and so does the PII-stripping/secret-ballot behavior
        // it drives) as soon as the board says "we are a condo/HOA", even if
        // the checklist itself came back "warnings" or "non-compliant".
        compliance_chapter: complianceChapter,
        // Carry the wizard's own checklist answers straight into the fields
        // they're actually confirming, so the checklist isn't just decorative.
        board_approved: wizardAnswers.board_resolution === "yes" ? 1 : 0,
        board_approved_by: wizardAnswers.board_resolution === "yes"
          ? Number(localStorage.getItem("userId")) || null
          : null,
        // A chapter applying means consent-gating should actually be
        // enforced going forward, regardless of whether the self-reported
        // "owner_consent" checklist item was answered yes.
        require_consent: complianceChapter ? 1 : (wizardAnswers.owner_consent === "yes" ? 1 : 0),
      });
      setWizardOpen(false);
      setSelectedElection(res.data);
      fetchElections();
    } catch (err) {
      console.error("Error creating election:", err);
      setSnackbarMessage("Failed to create election. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleBack = () => {
    setSelectedElection(null);
    fetchElections();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(47,99,173,0.08)",
              color: "#2f63ad",
            }}
          >
            <HowToVoteIcon sx={{ fontSize: 30 }} />
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#223142",
                mb: 0.25,
              }}
            >
              E-Voting System
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Manage elections, review existing ballots, and create new voting events.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {selectedElection ? (
        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
          }}
        >
          <ElectionPage election={selectedElection} onBack={handleBack} />
        </Card>
      ) : (
        <>
          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 2.5 },
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#223142",
                    mb: 0.5,
                  }}
                >
                  Elections
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Create and manage elections from one place.
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setWizardOpen(true)}
                sx={{
                  backgroundColor: "#2f63ad",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.25,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#275592",
                    boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                  },
                }}
              >
                Create New Election
              </Button>
            </Box>
          </Card>

          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
            }}
          >
            <ElectionList
              elections={elections}
              onSelectElection={(e) => setSelectedElection(e)}
            />
          </Card>
        </>
      )}

      <ComplianceWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreate={handleCreateElection}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 3 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EVoting;